import { Box, Flex, Icon, Tooltip } from "@nulogy/components";
import ThinDivider from "components/common/ThinDivider";
import { formatDate, formatDuration, formatQuantity, NO_DATA_LABEL } from "components/helpers/format";
import DraggableCardTruncatedText from "components/pages/schedule/workOrderCards/DraggableCardTruncatedText";
import ProductionProgressBar from "components/pages/schedule/workOrderCards/ProductionProgressBar";
import WorkOrder from "domain/WorkOrder";
import React from "react";
import styled from "styled-components";
import { transitionDefinition } from "./helpers/workOrderCardAnimation";

const Information = styled(Box)`
  display: grid;
  grid-template-columns: 108px auto;
  column-gap: 2px;
  row-gap: 2px;
  max-height: ${({ hidden = false }) => (hidden ? "0px" : "400px")};
  overflow: hidden;
  transition: ${transitionDefinition};
`;

const ErrorWrapper = styled.div(({ theme }) => ({
  color: theme.colors.red,
  height: "16px",
  lineHeight: "16px",
  verticalAlign: "middle",
}));

const ErrorSpan = styled.span`
  position: relative;
  bottom: 4px;
  margin-left: 3px;
`;

const ErrorTooltip = styled(Tooltip)({
  textAlign: "left",
  whiteSpace: "normal",
});

const QuantityError = ({ prefix }) => (
  <ErrorWrapper>
    <ErrorTooltip tooltip="This work order is missing data and cannot be scheduled.">
      <Icon icon="error" size="16px" />
    </ErrorTooltip>
    <ErrorSpan>{prefix} is 0</ErrorSpan>
  </ErrorWrapper>
);

const FieldInformation = ({ label, value, settingKey, settings, grabbable }) => {
  if (!label) {
    return null;
  }
  const dataTestIdValue = `${label.replace(/\s/g, "-").toLowerCase()}-value`;
  const dataTestIdLabel = `${label.replace(/\s/g, "-").toLowerCase()}-label`;

  if (settings && !settings[settingKey]) return null;

  return (
    <>
      <DraggableCardTruncatedText color="midGrey" data-testid={dataTestIdLabel} grabbable={grabbable}>
        {label}
      </DraggableCardTruncatedText>
      <DraggableCardTruncatedText data-testid={dataTestIdValue} grabbable={grabbable}>
        {value ?? NO_DATA_LABEL}
      </DraggableCardTruncatedText>
    </>
  );
};
const getPercentageLabel = ({ producedQuantity, quantity }) => {
  if (producedQuantity && !quantity) return "% cannot be calculated";
  if (!quantity || !producedQuantity) return "0% complete";

  const roundedProducedQuantity = formatQuantity(producedQuantity);
  const roundedQuantity = formatQuantity(quantity);
  const producedPercentage = Math.min(Math.floor((roundedProducedQuantity / roundedQuantity) * 100), 100);

  return `${producedPercentage}% complete`;
};

const ToBeScheduledValue = ({ workOrder }) => {
  const { unscheduledQuantity, unitOfMeasure, quantity, remainingDuration, standardProductionRate } = workOrder;
  const roundedUnscheduledQuantity = WorkOrder.isScheduledCompletedOrCancelled(workOrder)
    ? 0
    : formatQuantity(Math.abs(unscheduledQuantity));
  const remainingTime = WorkOrder.isScheduledCompletedOrCancelled(workOrder)
    ? formatDuration(0)
    : formatDuration(remainingDuration);

  return (
    <>
      {quantity > 0 &&
        standardProductionRate > 0 &&
        `${remainingTime} • ${roundedUnscheduledQuantity} ${unitOfMeasure}`}
      {standardProductionRate <= 0 && <QuantityError prefix="Production rate" />}
      {quantity <= 0 && <QuantityError prefix="Work order quantity" />}
    </>
  );
};

const ToBeScheduledField = ({ workOrder }) => {
  const toBeScheduledLabel = WorkOrder.isOverscheduled(workOrder) ? "Overscheduled by" : "To be scheduled";

  return (
    <FieldInformation
      grabbable={workOrder.schedulable}
      label={toBeScheduledLabel}
      value={<ToBeScheduledValue workOrder={workOrder} />}
    />
  );
};

const DueDateField = ({ due }) => {
  if (!due) return <>{NO_DATA_LABEL}</>;

  const overdue = WorkOrder.isOverdue({ due });

  return (
    <Flex gap="half" height="x2">
      {overdue && (
        <Box>
          <Icon color="red" icon="calendarClock" size="x2" />
        </Box>
      )}
      <Box color={overdue ? "red" : null}>{formatDate(due)}</Box>
    </Flex>
  );
};

export default function WorkOrderCardInformation({ workOrder, layoutSetting, expanded, expandedFields }) {
  const roundedQuantity = formatQuantity(workOrder.quantity);
  const roundedProducedQuantity = formatQuantity(workOrder.producedQuantity);
  const roundedScheduledQuantity = formatQuantity(workOrder.scheduledQuantity);

  const scheduledTime = formatDuration(workOrder.scheduledDuration);
  const percentageLabel = getPercentageLabel({
    producedQuantity: workOrder.producedQuantity,
    quantity: workOrder.quantity,
  });

  return (
    <Box>
      <Information>
        <FieldInformation
          grabbable={workOrder.schedulable}
          label="Due date"
          value={<DueDateField due={workOrder.due} />}
        />
        <FieldInformation
          grabbable={workOrder.schedulable}
          label="Item code"
          settingKey="itemCode"
          settings={layoutSetting}
          value={workOrder.itemCode}
        />
        <FieldInformation
          grabbable={workOrder.schedulable}
          label="Item description"
          settingKey="itemDescription"
          settings={layoutSetting}
          value={workOrder.itemDescription}
        />
      </Information>
      <Information hidden={!expanded}>
        {expandedFields.map(({ label, value, settingKey }) => (
          <FieldInformation
            grabbable={workOrder.schedulable}
            key={label}
            label={label}
            settingKey={settingKey}
            settings={layoutSetting}
            value={value}
          />
        ))}
      </Information>
      <ThinDivider />
      <Information>
        <ToBeScheduledField workOrder={workOrder} />
        <FieldInformation
          grabbable={workOrder.schedulable}
          label="Scheduled"
          value={`${scheduledTime} • ${roundedScheduledQuantity} ${workOrder.unitOfMeasure}`}
        />
        <FieldInformation
          grabbable={workOrder.schedulable}
          label="Produced"
          value={`${roundedProducedQuantity}/${roundedQuantity} ${workOrder.unitOfMeasure} • ${percentageLabel}`}
        />
        <ProductionProgressBar workOrder={workOrder} />
      </Information>
    </Box>
  );
}
